/**
 * Mailing settings — the SMTP relay, per-language senders, the send throttle,
 * and the OpenAI key personalised campaigns need.
 *
 * The password comes back masked and the mask is sent straight back when the
 * user doesn't retype it; the backend treats that value as "unchanged", so an
 * unrelated edit can never blank the stored secret. The OpenAI key follows the
 * same rule.
 */
define('mailingSettingsView',[
  'module',
  'app',
  'underscore',
  'jquery',
  'marionette',
  'mailingShell',
  'entities/mailing'
], function (
  module,
  App,
  _,
  $,
  Marionette,
  Shell,
  MailingApi
) {
  'use strict';

  /** The zones customers' recipients realistically live in; whatever is stored is always offered too. */
  var TIME_ZONES = [
    'Europe/Brussels', 'Europe/Amsterdam', 'Europe/Paris', 'Europe/Berlin', 'Europe/Luxembourg',
    'Europe/Madrid', 'Europe/Rome', 'Europe/Vienna', 'Europe/Zurich', 'Europe/Copenhagen',
    'Europe/London', 'Europe/Dublin', 'Europe/Lisbon', 'Europe/Helsinki', 'UTC'
  ];

  var SettingsView = Marionette.ItemView.extend({

    className: 'mailing-page mailing-settings',

    template: _.template(
      '<%= nav %>' +
      '<div class="mlg-body"><div class="mlg-loading">Loading settings…</div></div>'
    ),

    ui: {
      body: '.mlg-body'
    },

    events: {
      'click .mlg-save-settings': 'onSave',
      'click .mlg-test-smtp': 'onTest'
    },

    serializeData: function () {
      return {
        nav: Shell.navHtml('settings',
          '<button type="button" class="mlg-btn mlg-test-smtp">Test connection</button>' +
          '<button type="button" class="mlg-btn primary mlg-save-settings">Save</button>')
      };
    },

    onShow: function () {
      this.load();
    },

    load: function () {
      var self = this;
      MailingApi.settings().done(function (settings) {
        self.renderForm(settings || {});
      }).fail(function (xhr) {
        self.ui.body.html('<div class="mlg-error">' + _.escape(MailingApi.errorMessage(xhr)) + '</div>');
      });
    },

    renderForm: function (settings) {
      var secure = settings.smtpSecure !== 'false';
      this.ui.body.html(
        '<div class="mlg-settings-card">' +
        '<h3>SMTP relay</h3>' +
        '<div class="mlg-row">' +
        '  <label class="grow">Host<input type="text" class="f-host" value="' +
             _.escape(settings.smtpHost || '') + '"></label>' +
        '  <label class="narrow">Port<input type="number" class="f-port" value="' +
             _.escape(settings.smtpPort || '587') + '"></label>' +
        '</div>' +
        '<div class="mlg-row">' +
        '  <label class="grow">Username<input type="text" class="f-user" value="' +
             _.escape(settings.smtpUser || '') + '"></label>' +
        '  <label class="grow">Password<input type="password" class="f-password" value="' +
             _.escape(settings.smtpPassword || '') + '"></label>' +
        '</div>' +
        '<div class="mlg-row">' +
        '  <label class="grow">Sender name<input type="text" class="f-from-name" value="' +
             _.escape(settings.smtpFromName || '') + '"></label>' +
        '  <label class="check"><input type="checkbox" class="f-secure"' + (secure ? ' checked' : '') +
             '> Use STARTTLS</label>' +
        '</div>' +
        '<div class="mlg-hint">The password is stored encrypted and never sent back to this screen. ' +
          'Leave the dots untouched to keep the current one.</div>' +
        '</div>' +

        '<div class="mlg-settings-card">' +
        '<h3>Sender per language</h3>' +
        '<div class="mlg-hint">Used when the recipient\'s language matches and the campaign sets no sender ' +
          'of its own. Blank falls back to the sender name and username above.</div>' +
        '<table class="mlg-table compact mlg-senders"><thead><tr>' +
        '<th>Language</th><th>Sender name</th><th>Sender email</th></tr></thead><tbody>' +
        _.map(MailingApi.senderLanguages(), function (language) {
          return '<tr data-language="' + language + '">' +
            '<td class="c-lang">' + language + '</td>' +
            '<td><input type="text" class="f-lang-name" value="' +
              _.escape(settings['fromName' + language] || '') + '"></td>' +
            '<td><input type="email" class="f-lang-email" value="' +
              _.escape(settings['fromEmail' + language] || '') + '"></td>' +
            '</tr>';
        }).join('') +
        '</tbody></table>' +
        '</div>' +

        '<div class="mlg-settings-card">' +
        '<h3>OpenAI (personalised campaigns)</h3>' +
        '<label>API key<input type="password" class="f-openai-key" autocomplete="off" placeholder="sk-…" value="' +
          _.escape(settings.openaiApiKey || '') + '"></label>' +
        '<div class="mlg-hint">Each recipient of a personalised campaign costs one image generation on this ' +
          'key. Stored encrypted, like the SMTP password.</div>' +
        '<label class="narrow">Images generated in parallel<input type="number" min="1" max="10" ' +
          'class="f-parallel" value="' + _.escape(settings.personalisedParallel || '1') + '"></label>' +
        '<div class="mlg-hint">One image takes 30–90 s. At 1, a 100-contact campaign needs about an hour and a ' +
          'half; at 5, about twenty minutes — and five times the OpenAI rate. Max 10; mind the rate limits of ' +
          'your OpenAI tier.</div>' +
        '<div class="mlg-hint">A contact\'s brand is looked up on their company website: the one set on the ' +
          'contact, else their email domain — unless it is a free-mail one (gmail.com…). That list is the ' +
          'platform\'s, shared with company linking: <a href="#admin/genericemaildomains">Generic email ' +
          'domains</a>.</div>' +
        '</div>' +

        '<div class="mlg-settings-card">' +
        '<h3>Sending rate</h3>' +
        '<div class="mlg-row">' +
        '  <label class="narrow">Max per hour<input type="number" min="1" class="f-max-per-hour" value="' +
             _.escape(settings.queueMaxPerHour || '100') + '"></label>' +
        '  <label class="narrow">Seconds between sends<input type="number" min="0" step="0.5" ' +
             'class="f-delay" value="' + _.escape(settings.queueDelaySeconds || '2') + '"></label>' +
        '</div>' +
        '<label>Time zone of the sending windows<select class="f-timezone">' +
          _.map(_.uniq([settings.sendWindowTimezone || 'Europe/Brussels'].concat(TIME_ZONES)), function (zone) {
            return '<option' + (zone === (settings.sendWindowTimezone || 'Europe/Brussels') ? ' selected' : '') + '>' +
              _.escape(zone) + '</option>';
          }).join('') + '</select></label>' +
        '<div class="mlg-hint">A campaign\'s "09:00–17:30" is read on this clock, summer time included — not on ' +
          'the server\'s.</div>' +
        '<div class="mlg-hint">The outbox is drained once a minute, at most 20 emails per pass, ' +
          'never exceeding the hourly limit. Both guards protect the relay\'s reputation.</div>' +
        '</div>');
    },

    onSave: function () {
      var self = this;
      var $body = this.ui.body;
      var data = {
        smtpHost: $body.find('.f-host').val(),
        smtpPort: $body.find('.f-port').val(),
        smtpUser: $body.find('.f-user').val(),
        smtpPassword: $body.find('.f-password').val(),
        smtpFromName: $body.find('.f-from-name').val(),
        smtpSecure: $body.find('.f-secure').is(':checked') ? 'true' : 'false',
        queueMaxPerHour: $body.find('.f-max-per-hour').val(),
        queueDelaySeconds: $body.find('.f-delay').val(),
        sendWindowTimezone: $body.find('.f-timezone').val(),
        openaiApiKey: $body.find('.f-openai-key').val(),
        personalisedParallel: $body.find('.f-parallel').val()
      };
      $body.find('.mlg-senders tbody tr').each(function () {
        var $row = $(this);
        data['fromName' + $row.data('language')] = $row.find('.f-lang-name').val();
        data['fromEmail' + $row.data('language')] = $row.find('.f-lang-email').val();
      });
      MailingApi.updateSettings(data).done(function (settings) {
        Shell.banner(self.$el, 'Settings saved', 'ok');
        self.renderForm(settings || {});
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    /** Tests the *stored* credentials, so save before testing a change. */
    onTest: function () {
      var self = this;
      MailingApi.testSmtp().done(function (result) {
        Shell.banner(self.$el, result.message || 'SMTP connection successful', 'ok');
      }).fail(function (xhr) {
        var body = xhr.responseJSON || {};
        Shell.banner(self.$el, body.error || MailingApi.errorMessage(xhr), 'error');
      });
    }
  });

  module.exports = SettingsView;
});

